import { styled } from 'styled-components';

import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import {
  colors,
  cssBreakpoints,
  gutterWidth,
  maxWidth,
  spacing,
  transition,
} from '#/theme/theme';

const pageFooterEsPtDesktopSectionWidth = 130;
const pageFooterMobileSectionWidth = 130;
const pageFooterMobileGap = 30;

export const PageFooterHolder = styled.footer<{
  $showDivider: boolean;
}>`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: ${maxWidth[3]}px;
  max-width: 100%;
  padding-left: ${gutterWidth / 2}px;
  padding-right: ${gutterWidth / 2}px;
  padding-bottom: 100px;

  ${(props) => props.$showDivider && `padding-top: 70px;`};

  @media screen and (min-width: ${cssBreakpoints.mdUp}) {
    padding-left: ${gutterWidth}px;
    padding-right: ${gutterWidth}px;
    padding-bottom: 20px;
  }

  @media print {
    display: none;
  }
`;

export const PageFooterSocialLink = styled(ButtonOrLink)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 100%;
  background: ${colors.secondarySkyBlue};
  transition: ${transition};

  &:hover,
  &:focus {
    background: ${colors.hoverBlue};
  }
`;

export const PageFooterSectionsAndLogoMissionHolder = styled.div<{
  $showDivider: boolean;
}>`
  display: flex;
  padding-top: 70px;
  ${(props) =>
    props.$showDivider && `border-top: 1px solid ${colors.selectionGrey};`};

  @media all and (max-width: ${cssBreakpoints.lgDown}) {
    flex-direction: column;
  }
`;

export const PageFooterSectionsHolder = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;

  @media all and (min-width: ${cssBreakpoints.lgUp}) {
    margin-right: 56px;
  }

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    flex-wrap: wrap;
    gap: ${pageFooterMobileGap}px;
    align-self: center;
    width: ${pageFooterMobileSectionWidth * 2 + pageFooterMobileGap}px;

    .item:nth-child(2n + 1) {
      clear: both;
    }
  }
`;

export const PageFooterSectionHolder = styled.div<{ $isEsOrPt: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: ${(props) =>
    props.$isEsOrPt && `${pageFooterEsPtDesktopSectionWidth}px`};
  flex-shrink: 0;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    width: ${pageFooterMobileSectionWidth}px;
    gap: ${spacing[16]};
  }
`;

export const PageFooterSectionTitle = styled.div`
  font-weight: normal;
  color: ${colors.mediumContentGrey};
  font-size: 20px;
  margin: 0;
`;

export const PageFooterSectionLinksHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    gap: ${spacing[16]};
  }

  @media all and (width <= 400px) {
    gap: 10px;
  }
`;

export const PageFooterSectionLink = styled(ButtonOrLink)`
  font-size: 16px;
  color: ${colors.lightContentGrey};

  &:not(:hover) {
    text-decoration: none;
  }
`;

export const PageFooterLogoMissionHolder = styled.div<{
  $showDivider: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-basis: 25%;

  @media all and (max-width: ${cssBreakpoints.lgDown}) {
    align-items: center;
    margin-top: 30px;
    padding-left: ${gutterWidth}px;
    padding-right: ${gutterWidth}px;
    padding-top: 30px;
    border-top: 1px solid ${colors.selectionGrey};
  }
`;

export const PageFooterLogoLink = styled(ButtonOrLink)`
  margin-bottom: 24px;

  @media all and (max-width: ${cssBreakpoints.lgDown}) {
    display: none;
  }
`;

export const PageFooterMissionStatement = styled.p`
  font-size: 16px;
  line-height: 28.5px;
  margin: 0;
  color: ${colors.lightContentGrey};

  @media all and (max-width: ${cssBreakpoints.lgDown}) {
    text-align: center;
    width: 100%;
  }
`;

export const PageFooterSocialLinksHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media all and (max-width: ${cssBreakpoints.lgDown}) {
    margin-top: 40px;
  }

  @media all and (min-width: ${cssBreakpoints.lgUp}) {
    margin-top: 40px;
  }
`;

export const PageFooterBottom = styled.div`
  display: flex;
  margin-top: 40px;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    align-items: center;
    justify-content: space-between;
  }
`;

export const PageFooterLocaleLinksHolder = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[24]};

  > *:not(:last-child) {
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: -12px;
      height: 16px;
      width: 1px;
      background: currentcolor;
      pointer-events: none;
    }
  }
`;

export const PageFooterLocaleLink = styled(ButtonOrLink)`
  display: inline-flex;
  position: relative;
  font-size: 16px;
  font-weight: normal;
  color: ${colors.lightContentGrey};
  text-decoration: none;

  &:is(div) {
    opacity: 0.5;
  }
`;

export const PageFooterCopyright = styled.div`
  font-size: 16px;
  color: ${colors.lightContentGrey};

  @media all and (min-width: ${cssBreakpoints.lgUp}) {
    flex-basis: 25%;
  }
`;
